// "Office 365 disconnected — reconnect" — the VISIBLE, self-serve recovery
// for a dropped Microsoft Graph connection.
//
// Why this exists (Nate, a Realty advisor, 2026-07-06): partway through
// using the OS "something just disconnected" — his calendar stopped loading
// and his tasks vanished, with NO signal that the cause was a dropped
// Microsoft connection. Under the hood, a token-refresh failure flips the
// connection row to status='error' (or 'expired'/'subscription_lost'); every
// Graph-backed endpoint then answers 412 not_connected, and the surfaces
// that consume them (the calendar tile, the To Do task rows, the mailbox)
// went silently empty. This banner turns that silent state into a loud,
// one-click-fixable one.
//
// Mounted ONCE, app-wide, in PageLayout next to the impersonation banner —
// a Graph disconnect breaks email + calendar + tasks on EVERY page, so a
// single global banner is the right surface (and it keeps this out of any
// one dashboard's internals).
//
// Distinct from ConnectEmailBanner: that one is a gentle, dismissible
// "connect your email" nudge for users who never connected. This one is a
// non-dismissible alert for a connection that WAS working and broke — the
// two never show at once (connect-email-banner-helpers defers to this for
// broken states). The status→banner split lives in reconnect-office365-helpers.
//
// Two triggers keep it timely without polling:
//   1. A status fetch on mount (catches Nate's "I loaded the app and it was
//      already broken" case).
//   2. A global 412-not_connected signal from api.ts (catches the
//      mid-session break — the moment a Graph call 412s, we re-check status
//      and surface if it's genuinely broken). Mirrors the existing
//      setUnauthorizedHandler pattern for 401s.

import { useCallback, useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";

import { api, setGraphConnectionLostHandler } from "../api";
import {
  GRAPH_CONNECT_URL,
  reconnectReasonLine,
  shouldShowReconnectBanner,
} from "./reconnect-office365-helpers";

export function ReconnectOffice365Banner() {
  // null = unknown (loading or the status call failed); a broken state is an
  // explicit "expired" | "error" | "subscription_lost".
  const [status, setStatus] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const r = await api.getMicrosoftGraphStatus();
      setStatus(r.connection?.status ?? null);
    } catch {
      // A failed status check leaves us in the unknown state so the banner
      // stays hidden rather than crying wolf on a transient hiccup.
      setStatus(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void checkStatus();
  }, [checkStatus]);

  // A Graph-backed API call just answered 412 not_connected somewhere in the
  // app (calendar tile, tasks, mailbox). Re-check status so a mid-session
  // break surfaces immediately. We re-fetch rather than assume-broken because
  // a genuinely never-connected user ALSO gets 412 here — for them the fetch
  // returns status=null and this banner correctly stays hidden (the gentle
  // ConnectEmailBanner covers that case).
  useEffect(() => {
    setGraphConnectionLostHandler(() => {
      void checkStatus();
    });
    return () => setGraphConnectionLostHandler(null);
  }, [checkStatus]);

  if (!shouldShowReconnectBanner({ loaded, status })) return null;

  function reconnect() {
    // Browser redirect — the backend issues a 302 into the Microsoft OAuth
    // flow, then returns to /preferences?graph_success=1 (a full page load,
    // so this banner re-checks and hides itself).
    window.location.href = GRAPH_CONNECT_URL;
  }

  return (
    <div
      role="status"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        border: "1px solid var(--red, #c0392b)",
        background: "var(--red-tint, rgba(192,57,43,0.1))",
        borderRadius: 12,
        padding: "12px 16px",
        margin: "12px 16px 0",
      }}
    >
      <span
        style={{
          color: "var(--red, #c0392b)",
          display: "inline-flex",
          flexShrink: 0,
        }}
      >
        <AlertTriangle size={18} strokeWidth={1.8} aria-hidden />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontFamily: "Gotham, Montserrat, sans-serif",
            fontWeight: 800,
            fontSize: 12.5,
            letterSpacing: "0.05em",
            color: "var(--navy, #022c51)",
          }}
        >
          Office 365 disconnected — reconnect
        </div>
        <div style={{ fontSize: 12, color: "var(--fg-3, #333)", marginTop: 2 }}>
          {reconnectReasonLine(status)} Your Outlook calendar, Microsoft To Do
          tasks, and email won't load until you reconnect. This is a Microsoft
          connection issue — you're still signed in to Stewardship OS.
        </div>
      </div>
      <button
        type="button"
        onClick={reconnect}
        style={{
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          border: "none",
          cursor: "pointer",
          background:
            "linear-gradient(135deg, var(--blue, #4e98c6), var(--blue-dark, #3a7ea8))",
          color: "var(--fg-on-navy, #fff)",
          borderRadius: 8,
          padding: "8px 14px",
          fontFamily: "Gotham, Montserrat, sans-serif",
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        Reconnect Office 365 →
      </button>
    </div>
  );
}
